import { Injectable, inject } from '@angular/core';
import { singleConnect } from '@core/utils';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import {
	Action,
	State,
	StateToken,
	createPropertySelectors,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { map, of, retry, timer } from 'rxjs';
import { arrayUnion, arrayRemove } from '@angular/fire/firestore';

import { ClientIntakeFormContextSubCollectionRepo } from '../clientIntakeFormContext.firebase.repo';

import type { ClientIntakeFormContextStateModel } from './clientIntakeFormContext.firebase.models';
import {
	IntakeFormContextFsDocType,
	type ClientIntakeFormContextItem,
	type ClientIntakeFormType,
	type ClientIntakeMappedClient,
} from '@models/client-intake';
import {
	ClearClientIntakeFormContextConnectionAndState,
	SetClientIntakeFormContext,
	SetClientIntakeFormContextConnection,
	UpsertClientIntakeFormContext,
	UpdateClientIntakeFormContextItem,
	DeleteClientIntakeFormContextItem,
	AddClientsToIntakeFormContext,
	RemoveClientsFromIntakeFormContext,
} from './clientIntakeFormContext.firebase.actions';

export const CLIENT_INTAKE_FORM_CONTEXT_STATE_TOKEN =
	new StateToken<ClientIntakeFormContextStateModel>('clientIntakeFormContext');

export const $PROPS = createPropertySelectors(
	CLIENT_INTAKE_FORM_CONTEXT_STATE_TOKEN,
);

export const CLIENT_INTAKE_FORM_CONTEXT_STATE_DEFAULTS: ClientIntakeFormContextStateModel =
	{
		query: {
			connected: false,
			pathParams: null,
		},
		items: [],
	};

@State<ClientIntakeFormContextStateModel>({
	name: CLIENT_INTAKE_FORM_CONTEXT_STATE_TOKEN,
	defaults: CLIENT_INTAKE_FORM_CONTEXT_STATE_DEFAULTS,
})
@Injectable()
export class ClientIntakeFormContextState implements NgxsOnInit {
	private subCollectionRepo = inject(ClientIntakeFormContextSubCollectionRepo);
	static _props = $PROPS;

	ngxsOnInit(): void {
		singleConnect(
			ClientIntakeFormContextState._props.query,
			(query) => {
				if (
					!query.connected ||
					!query.pathParams?.businessId ||
					!query.pathParams?.matterId ||
					!query.pathParams?.clientIntakeFormId
				) {
					return of([] as ClientIntakeFormContextItem[]);
				}
				return this.subCollectionRepo
					.getCollection(query.pathParams)
					.getAll()
					.pipe(
						map((data) => this.convertFromDatabaseFormat(data)),
						retry({
							count: 10,
							delay: (error, retryCount) => {
								const backoffTime = Math.min(
									500 * Math.pow(2, retryCount - 1),
									30000,
								);
								return timer(backoffTime);
							},
						}),
					);
			},
			(items) => {
				return new SetClientIntakeFormContext(items);
			},
		);
	}

	private convertFromDatabaseFormat(
		data: Array<{ id?: string } & Record<string, unknown>>,
	): ClientIntakeFormContextItem[] {
		return data
			.map((doc) => {
				const docId = doc.id;
				if (!docId) return null;

				if (docId === IntakeFormContextFsDocType.CLIENT_TAGS) {
					return {
						type: 'clients' as const,
						id: IntakeFormContextFsDocType.CLIENT_TAGS as const,
						data: doc as { all: ClientIntakeMappedClient[] },
					};
				}

				if (docId === IntakeFormContextFsDocType.ON_MATTER_UPDATE) {
					return {
						type: 'general' as const,
						id: IntakeFormContextFsDocType.ON_MATTER_UPDATE as const,
						data: doc as {
							id: string;
							name: string;
							type: ClientIntakeFormType;
							hasPersonClients?: boolean;
							hasCorporationClients?: boolean;
						},
					};
				}

				return null;
			})
			.filter((item): item is ClientIntakeFormContextItem => item !== null);
	}

	@Action(SetClientIntakeFormContextConnection)
	setClientIntakeFormContextConnection(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		action: SetClientIntakeFormContextConnection,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: action.connect,
					pathParams: action.pathParams,
				}),
			}),
		);
	}

	@Action(ClearClientIntakeFormContextConnectionAndState)
	clearClientIntakeFormContextConnection(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: false,
					pathParams: null,
				}),
				items: [],
			}),
		);
	}

	@Action(SetClientIntakeFormContext)
	setClientIntakeFormContext(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		{ items }: SetClientIntakeFormContext,
	): void {
		ctx.patchState({
			items,
		});
	}

	@Action(UpsertClientIntakeFormContext)
	async upsertClientIntakeFormContext(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		{ items }: UpsertClientIntakeFormContext,
	): Promise<void> {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams) return;

			if (!items || items.length === 0) return;

			const batchItems = items.map((item) => ({
				id: item.id,
				data: item.data,
			}));

			await this.subCollectionRepo
				.getCollection(pathParams)
				?.setBatch(batchItems);
		} catch (err) {
			console.error(`Error upserting client intake form context:`, err);
		}
	}

	@Action(UpdateClientIntakeFormContextItem)
	updateClientIntakeFormContextItem(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		{ itemId, updates }: UpdateClientIntakeFormContextItem,
	): void {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams || !itemId) return;

			void this.subCollectionRepo
				.getCollection(pathParams)
				?.update(itemId, updates);
		} catch (err) {
			console.error(
				`Error updating client intake form context item (ID: ${itemId}):`,
				err,
			);
		}
	}

	@Action(DeleteClientIntakeFormContextItem)
	deleteClientIntakeFormContextItem(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		{ itemId }: DeleteClientIntakeFormContextItem,
	): void {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams || !itemId) return;

			void this.subCollectionRepo.getCollection(pathParams)?.delete(itemId);
		} catch (err) {
			console.error(
				`Error deleting client intake form context item (ID: ${itemId}):`,
				err,
			);
		}
	}

	@Action(AddClientsToIntakeFormContext)
	async addClientsToContext(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		{ clients }: AddClientsToIntakeFormContext,
	): Promise<void> {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams || clients.length === 0) return;

			await this.subCollectionRepo
				.getCollection(pathParams)
				?.update('clientTags', { all: arrayUnion(...clients) });
		} catch (err) {
			console.error(`Error adding clients to intake form context:`, err);
		}
	}

	@Action(RemoveClientsFromIntakeFormContext)
	async removeClientsFromContext(
		ctx: StateContext<ClientIntakeFormContextStateModel>,
		{ clients }: RemoveClientsFromIntakeFormContext,
	): Promise<void> {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams || clients.length === 0) return;

			await this.subCollectionRepo
				.getCollection(pathParams)
				?.update('clientTags', { all: arrayRemove(...clients) });
		} catch (err) {
			console.error(`Error removing clients from intake form context:`, err);
		}
	}
}
